import { ToastBaseProps, useToastContext } from '@ingrid/luterkort-react';

import { useTranslation } from '~src/i18n/use-translation';

interface ToastContent {
  subtitle?: string;
  title: string;
}

type ToastFnOption<TData, TError = Error> = ((data?: TData | TError) => ToastContent) | ToastContent | false;

interface ToastPromiseFnOptions<TData> {
  success: ToastFnOption<TData>;
  error?: ToastFnOption<TData>;
  loading?: ToastFnOption<TData>;
}

export function useToast() {
  const { generateId, updateToast: setToast } = useToastContext();
  const { t } = useTranslation();

  function toastFn(toast: ToastBaseProps) {
    setToast({
      id: generateId(),
      duration: 5000,
      ...toast,
    });
  }

  async function toastPromiseFn<TData = unknown>(
    promise: Promise<TData> | (() => Promise<TData>),
    options: ToastPromiseFnOptions<TData>
  ): Promise<TData | null> {
    const id = generateId();

    function defaultTitle<TKey extends keyof typeof options>(key: TKey) {
      switch (key) {
        case 'loading':
          return t('common.loading');
        case 'error':
          return t('errors.title');
        default:
          return;
      }
    }

    function parseOptions<TKey extends keyof typeof options>(
      key: TKey,
      data?: TData | Error
    ): ToastContent | undefined {
      const option = options[key];
      if (typeof option === 'function') {
        return { ...option(data) };
      }

      if (typeof option === 'object') {
        return option;
      }

      if (typeof option === 'undefined' && data instanceof Error) {
        return { title: data.message };
      }

      const title = defaultTitle(key);
      if (typeof option === 'undefined' && title) {
        return { title };
      }

      return;
    }

    function maybeSetToast<TKey extends keyof typeof options>(key: TKey, data?: TData | Error) {
      const parsedOptions = parseOptions(key, data);
      if (parsedOptions) {
        setToast({
          id,
          ...parsedOptions,
          variant: key === 'loading' ? 'loading' : key === 'error' ? 'critical' : 'success',
          duration: key === 'loading' ? Number.POSITIVE_INFINITY : 5000,
        });
      }
    }

    maybeSetToast('loading');

    try {
      const result = await (typeof promise === 'function' ? promise() : promise);

      maybeSetToast('success', result);

      return result;
    } catch (error: unknown) {
      if (error instanceof Error) {
        maybeSetToast('error', error);

        throw error;
      } else {
        maybeSetToast('error');

        throw new Error(t('errors.title'));
      }
    }
  }

  const toast = Object.assign(toastFn, {
    promise: toastPromiseFn,
  });

  return {
    toast,
  };
}
